import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import type { ChallengeReduxState } from '../../types/models/challenges';

import { haNavigation, pages } from '../../assets/constants';
import { getChallengeType, shouldShowChallenge } from '../../shared/util/challenges';
import { useFetchChallengeQuery } from '../challengesApiSlice';
import { emptyChallenge, setChallenge } from '../challengesSlice';

interface RootState {
  challenges: { challenge: ChallengeReduxState };
  mediaSliders: { navigation: { title: string; code: string }[] };
}

export function useChallengePage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const pageid = window.location.pathname.split('/')[1];
  const section = pages.find((page) => page.url === pageid);
  const sectionid = section?.id;

  const [errorText, setErrorText] = useState<string | null>(null);
  const [isChallengeVisible, setIsChallengeVisible] = useState(false);
  const [isChallengeUnavailable, setIsChallengeUnavailable] = useState(false);
  const [showCompletedModal, setShowCompletedModal] = useState(false);

  const challenge = useSelector((state: RootState) => state?.challenges?.challenge);

  const {
    data: challengeData,
    isLoading,
    error,
  } = useFetchChallengeQuery(getChallengeType(sectionid), {
    skip: !getChallengeType(sectionid),
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (!isLoading && challengeData?.message === 'No challenge found') {
      setIsChallengeUnavailable(true);
    } else {
      setIsChallengeUnavailable(false);
    }
  }, [isLoading, challengeData]);

  useEffect(() => {
    if (!isLoading && !challengeData && !getChallengeType(sectionid)) {
      navigate(`/${pageid}/library`);
    }
  }, [isLoading, challengeData, sectionid, navigate, pageid]);

  useEffect(() => {
    setIsChallengeVisible(shouldShowChallenge(sectionid, challenge?.type));
  }, [sectionid, challenge]);

  useEffect(() => {
    if (error) {
      setErrorText(t('challenge.error.load'));
    }
    if (
      !isLoading &&
      !error &&
      challengeData &&
      !challengeData?.message &&
      challengeData?.start_date &&
      new Date().getTime() >= new Date(challengeData.start_date).getTime()
    ) {
      dispatch(setChallenge({ challenge: challengeData }));
    } else {
      dispatch(setChallenge({ challenge: emptyChallenge }));
    }
  }, [challengeData, isLoading, error, dispatch, t]);

  useEffect(() => {
    if (challenge?.isActive === null) return;
    if (!isLoading && !challenge?.isActive) {
      setShowCompletedModal(true);
    } else {
      setShowCompletedModal(false);
    }
  }, [challenge, isLoading]);

  const toggleActivePage = (tabid: number) => {
    if (tabid === 3) return;
    navigate(`/${pageid}/${haNavigation[tabid].title}`, {
      state: { from: 'challenge' },
    });
  };

  const handleCloseCompletedModal = () => setShowCompletedModal(false);

  return {
    challenge,
    isLoading,
    isChallengeVisible,
    isChallengeUnavailable,
    errorText,
    setErrorText,
    showCompletedModal,
    handleCloseCompletedModal,
    toggleActivePage,
    sectionid,
  };
}
